// frontend/src/pages/staff/StaffServiceBulkAdd.js
import React, { useState, useEffect, useCallback } from 'react';
import { prepareImageForUpload } from '../../utils/imageUploadPolicy';
import { useNavigate, useLocation } from 'react-router-dom';
import StaffSidebarLayout from './StaffSidebarLayout';
import axios from 'axios';
import ImageUploadField from '../../components/ImageUploadField';
import './staffStyles.css';

function StaffServiceBulkAdd() {
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Initialize with 5 empty service forms
  const [services, setServices] = useState([
    { name: '', description: '', price: '', durationMinutes: '', category: 'other', serviceImage: '', imageFile: null, imagePreview: null },
    { name: '', description: '', price: '', durationMinutes: '', category: 'other', serviceImage: '', imageFile: null, imagePreview: null },
    { name: '', description: '', price: '', durationMinutes: '', category: 'other', serviceImage: '', imageFile: null, imagePreview: null },
    { name: '', description: '', price: '', durationMinutes: '', category: 'other', serviceImage: '', imageFile: null, imagePreview: null },
    { name: '', description: '', price: '', durationMinutes: '', category: 'other', serviceImage: '', imageFile: null, imagePreview: null }
  ]);

  const [uploadingImages, setUploadingImages] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // All professionals use unified professional role type
  const getRoleType = useCallback(() => {
    return 'professional';
  }, []);

  const getBasePath = useCallback(() => {
    const roleType = getRoleType();
    return `/dashboard/${roleType}`;
  }, [getRoleType]);

  // Fetch service categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      try {
        const staffResponse = await axios.get(`${baseURL}/api/staff/me`, { headers });
        const staff = staffResponse.data.staff;

        if (staff.businessId) {
          const businessResponse = await axios.get(`${baseURL}/api/business/${staff.businessId}`, { headers });
          const businessCategory = businessResponse.data.business?.businessCategory;

          if (businessCategory) {
            const categoriesResponse = await axios.get(
              `${baseURL}/api/platform-settings/service-categories?businessCategories=${businessCategory}`
            );
            setServiceCategories(categoriesResponse.data.categories || []);
          }
        }
      } catch (err) {
        // Fallback to default categories
        setServiceCategories([
          { value: 'haircut', label: 'Haircut' },
          { value: 'color', label: 'Color' },
          { value: 'styling', label: 'Styling' },
          { value: 'treatment', label: 'Treatment' },
          { value: 'spa', label: 'Spa' },
          { value: 'nails', label: 'Nails' },
          { value: 'makeup', label: 'Makeup' },
          { value: 'waxing', label: 'Waxing' },
          { value: 'other', label: 'Other' }
        ]);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) {
      navigate('/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg('Configuration error. Please contact support.');
      return null;
    }
    return baseURL;
  }, []);

  const handleInputChange = (index, field, value) => {
    const updatedServices = [...services];
    updatedServices[index][field] = value;
    setServices(updatedServices);
    
    // Clear messages on input change
    setErrorMsg('');
    setSuccessMsg('');
  };

  // Files arrive already checked and prepared by ImageUploadField; keep an object-URL preview.
  const handleImageSelected = (index, file) => {
    if (!file) return;

    const updatedServices = [...services];
    updatedServices[index].imageFile = file;
    updatedServices[index].imagePreview = URL.createObjectURL(file);
    setServices(updatedServices);
    setErrorMsg('');
  };

  const uploadImages = async (servicesList) => {
    const headers = getAuthHeaders();
    const baseURL = getApiBaseUrl();
    if (!headers || !baseURL) return servicesList;

    const uploadedServices = await Promise.all(
      servicesList.map(async (svc, index) => {
        if (!svc.imageFile) return { ...svc, serviceImage: null };

        try {
          const formData = new FormData();
          formData.append('image', await prepareImageForUpload(svc.imageFile));

          const res = await axios.post(
            `${baseURL}/api/staff/services/upload-image`,
            formData,
            {
              headers: {
                ...headers,
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          return { ...svc, serviceImage: res.data.imageUrl };
        } catch (err) {
          throw new Error(`Failed to upload image for service ${index + 1}`);
        }
      })
    );

    return uploadedServices;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    setSaving(true);

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) {
        setSaving(false);
        return;
      }

      // Filter out empty services (those without a name)
      const validServices = services.filter(svc => svc.name.trim() !== '');

      if (validServices.length === 0) {
        setErrorMsg('Please add at least one service');
        setSaving(false);
        return;
      }

      // Validate each service
      for (let i = 0; i < validServices.length; i++) {
        const svc = validServices[i];
        
        if (!svc.price || parseFloat(svc.price) < 0) {
          setErrorMsg(`Service ${i + 1}: Valid price is required`);
          setSaving(false);
          return;
        }

        if (!svc.durationMinutes || parseInt(svc.durationMinutes) < 5) {
          setErrorMsg(`Service ${i + 1}: Duration must be at least 5 minutes`);
          setSaving(false);
          return;
        }
      }

      // Upload all images first
      setUploadingImages(true);
      let servicesWithImages;
      try {
        servicesWithImages = await uploadImages(validServices);
      } catch (err) {
        setErrorMsg(err.message);
        setSaving(false);
        setUploadingImages(false);
        return;
      }
      setUploadingImages(false);

      // Prepare services for API
      const servicesToCreate = servicesWithImages.map(svc => ({
        name: svc.name.trim(),
        description: svc.description.trim(),
        price: parseFloat(svc.price),
        durationMinutes: parseInt(svc.durationMinutes),
        category: svc.category,
        serviceImage: svc.serviceImage
      }));

      // Send bulk create request
      const res = await axios.post(
        `${baseURL}/api/staff/services/bulk`,
        { services: servicesToCreate },
        { headers }
      );

      setSuccessMsg(`${res.data.count} service(s) created successfully!`);
      
      // Redirect back to services list after 1.5 seconds
      setTimeout(() => {
        const basePath = getBasePath();
        navigate(`${basePath}/services`);
      }, 1500);
      
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem('ogestyle_token');
        navigate('/login');
      } else {
        const errorMessage = err?.response?.data?.message || err.message || 'Unable to create services. Please try again.';
        setErrorMsg(errorMessage);
        
        // Show specific validation errors if available
        if (err?.response?.data?.errors) {
          setErrorMsg(err.response.data.errors.join('; '));
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const basePath = getBasePath();
    navigate(`${basePath}/services`);
  };

  const roleType = getRoleType();
  const basePath = getBasePath();

  return (
    <StaffSidebarLayout roleType={roleType}>
      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto', 
        padding: '20px',
        paddingBottom: window.innerWidth < 768 ? '180px' : '60px'
      }}>
        {/* Back Button */}
        <button
          onClick={() => navigate(`${basePath}/services`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            marginBottom: '20px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            background: '#fff',
            color: '#333',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#f5f5f5';
            e.target.style.borderColor = '#001d4a';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#fff';
            e.target.style.borderColor = '#ddd';
          }}
        >
          <span>←</span> Back to Services
        </button>

        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ margin: '0 0 8px 0' }}>Bulk Add Services</h2>
          <p style={{ margin: 0, color: '#666' }}>Add up to 5 services at once. Leave fields blank to skip.</p>
          {/* Phase 5: Location context info */}
          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'start',
            gap: '8px'
          }}>
            <span style={{ fontSize: '16px', flexShrink: 0 }}>📍</span>
            <p style={{ margin: 0, fontSize: '13px', color: '#0369a1', lineHeight: '1.5' }}>
              All services will be added to your current active location.
            </p>
          </div>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div style={{ 
            background: '#fff3f3', 
            border: '1px solid #c00', 
            borderRadius: '8px', 
            padding: '15px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: 0, color: '#c00' }}>{errorMsg}</p>
          </div>
        )}

        {/* Success Message */}
        {successMsg && (
          <div style={{ 
            background: '#e6ffed', 
            border: '1px solid #03543f', 
            borderRadius: '8px', 
            padding: '15px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: 0, color: '#03543f' }}>{successMsg}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {services.map((service, index) => (
            <div 
              key={index} 
              className="dashboard-card" 
              style={{ 
                padding: '25px', 
                marginBottom: '20px',
                border: service.name ? '2px solid #001d4a' : '1px solid #e0e0e0'
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: '20px', color: '#001d4a' }}>
                Service {index + 1}
              </h4>

              {/* Service Name */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  marginBottom: '6px',
                  color: '#001d4a'
                }}>
                  Service Name {index === 0 && <span style={{ color: '#c00' }}>*</span>}
                </label>
                <input
                  type="text"
                  value={service.name}
                  onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                  maxLength={100}
                  required={index === 0}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g., Haircut & Style"
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  marginBottom: '6px',
                  color: '#001d4a'
                }}>
                  Description
                </label>
                <textarea
                  value={service.description}
                  onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                  maxLength={500}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="Describe this service..."
                />
              </div>

              {/* Price, Duration, Category */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '13px', 
                    fontWeight: '500', 
                    marginBottom: '6px',
                    color: '#001d4a'
                  }}>
                    Price ($) {index === 0 && <span style={{ color: '#c00' }}>*</span>}
                  </label>
                  <input
                    type="number"
                    value={service.price}
                    onChange={(e) => handleInputChange(index, 'price', e.target.value)}
                    required={service.name !== ''}
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '13px', 
                    fontWeight: '500', 
                    marginBottom: '6px',
                    color: '#001d4a'
                  }}>
                    Duration (min) {index === 0 && <span style={{ color: '#c00' }}>*</span>}
                  </label>
                  <input
                    type="number"
                    value={service.durationMinutes}
                    onChange={(e) => handleInputChange(index, 'durationMinutes', e.target.value)}
                    required={service.name !== ''}
                    min="5"
                    step="5"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="30"
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '13px', 
                    fontWeight: '500', 
                    marginBottom: '6px',
                    color: '#001d4a'
                  }}>
                    Category
                  </label>
                  <select
                    value={service.category}
                    onChange={(e) => handleInputChange(index, 'category', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: '#fff',
                      cursor: 'pointer',
                      boxSizing: 'border-box'
                    }}
                  >
                    {loadingCategories ? (
                      <option disabled>Loading...</option>
                    ) : (
                      serviceCategories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <ImageUploadField
                  variant="inline"
                  label="Service Image"
                  buttonLabel={service.imageFile ? 'Replace image' : 'Choose image'}
                  uploadOnSelect={false}
                  showPreviews={false}
                  onSelectFiles={(file) => handleImageSelected(index, file)}
                  onError={(message) =>
                    setErrorMsg(`Service ${index + 1}: ${message}`)
                  }
                />
                {service.imagePreview && (
                  <div style={{ marginTop: '10px' }}>
                    <img 
                      src={service.imagePreview} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: '150px', 
                        maxHeight: '100px', 
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '30px' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              style={{
                padding: '12px 30px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                background: '#fff',
                color: '#333',
                fontSize: '16px',
                fontWeight: '500',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || uploadingImages}
              className="btn-primary"
              style={{
                padding: '12px 30px',
                minWidth: '180px'
              }}
            >
              {uploadingImages ? 'Uploading Images...' : saving ? 'Creating...' : 'Create Services'}
            </button>
          </div>
        </form>
      </div>
    </StaffSidebarLayout>
  );
}

export default StaffServiceBulkAdd;
